// LexoRank — 用 base36 字符串做排序 key，相邻插入只追加字符串字典序中段。
//
// BASE = '0123456789abcdefghijklmnopqrstuvwxyz'，36 进制。
// 客户端阈值 LEXORANK_REBALANCE_THRESHOLD：单 key 长度 > 128 → 触发整桶 rebalance。
// 服务端 batch 工作的阈值见 LEXORANK_BATCH_TRIGGER_THRESHOLD（更早触发）。

const BASE = '0123456789abcdefghijklmnopqrstuvwxyz';
const MIN_CHAR = BASE[0]!;
const MAX_CHAR = BASE[BASE.length - 1]!;
const MID_CHAR = BASE[Math.floor(BASE.length / 2)]!;

export const LEXORANK_MIN = MIN_CHAR;
export const LEXORANK_MAX = MAX_CHAR;
export const LEXORANK_REBALANCE_THRESHOLD = 128;
// 服务端 batch worker 触发 rebalance 的阈值。比客户端小是有意为之 ——
// batch 异步空闲跑，宁可主动重整避免客户端单次 op 的 key 写得过长。
export const LEXORANK_BATCH_TRIGGER_THRESHOLD = 96;

function charIndex(ch: string): number {
  const idx = BASE.indexOf(ch);
  if (idx === -1) throw new Error(`Invalid lexorank char: ${ch}`);
  return idx;
}

/**
 * 返回 prev < result < next 的字符串（标准字典序）。
 *
 * - prev = null 视作"无下界"（虚拟全 MIN）。
 * - next = null 视作"无上界"（虚拟全 MAX）。
 * - 当 prev 是 next 的严格前缀且 next 的 tail 全是 MIN_CHAR（如 between('a','a0')）
 *   时，按字典序根本没有合法 key 能插进来 —— 这是调用方需要 rebalance 的信号，
 *   函数 throw。原实现会返回一个"看起来像但其实大于 next"的字符串（如 'a0h'），
 *   静默破坏排序不变量。
 */
export function lexorankBetween(prev: string | null, next: string | null): string {
  const a = prev ?? '';
  const b = next; // null = 无上界

  let result = '';
  let i = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // next 是有界字符串且我们已经把它整个匹配完 → 任何继续追加都会让 result > next
    if (b !== null && i >= b.length) {
      throw new Error(
        `lexorankBetween: cannot fit between "${a}" and "${b}" — caller should rebalance`,
      );
    }

    const aExhausted = i >= a.length;
    const ca = aExhausted ? MIN_CHAR : a[i]!;
    const cb = b === null || i >= b.length ? MAX_CHAR : b[i]!;
    const ia = charIndex(ca);
    const ib = charIndex(cb);

    if (ia > ib) {
      throw new Error(`lexorankBetween: prev="${a}" >= next="${b ?? '<null>'}"`);
    }

    if (ia === ib) {
      result += ca;
      i += 1;
      continue;
    }

    if (ib - ia >= 2) {
      // 严格中间值
      result += BASE[Math.floor((ia + ib) / 2)]!;
      return result;
    }

    // 相邻情况：ib === ia + 1。取 ca，然后必须把 result 延伸到比 a 大。
    // 此时 result === a[0..i] 同时 result < b（因 result[i] = ca = a[i] < b[i]），
    // 所以追加任何字符都不会越过 b。
    result += ca;
    i += 1;

    // 跨过 a 的尾部 MAX 段（这些位置必须照搬，单字符无法 > MAX）
    while (i < a.length && a[i] === MAX_CHAR) {
      result += a[i]!;
      i += 1;
    }
    if (i < a.length) {
      const ax = charIndex(a[i]!);
      // ax < MAX；插入 (ax, MAX] 的中点
      const mid = Math.floor((ax + (BASE.length - 1)) / 2);
      // ax = MAX-1 时 mid = ax，需要严格大 → 用 MAX_CHAR
      result += mid === ax ? MAX_CHAR : BASE[mid]!;
      return result;
    }
    // a 已耗尽（或尾部全 MAX），追加 MID_CHAR 保证 result > a
    result += MID_CHAR;
    return result;
  }
}

export function lexorankFirst(): string {
  return MID_CHAR;
}

export function lexorankAfter(prev: string): string {
  return lexorankBetween(prev, null);
}

export function lexorankBefore(next: string): string {
  return lexorankBetween(null, next);
}

export function needsRebalance(sortKey: string): boolean {
  return sortKey.length > LEXORANK_REBALANCE_THRESHOLD;
}

/**
 * 生成 count 个等距、单调递增的 base36 排序 key。
 *
 * 原实现反复取 (prev, null) 中点 → 序列几何收敛到 'z'，rebalance 之后没几次插入就
 * 又被推到极限。这里改成把 base36^k 空间均匀划分成 count+1 段。
 */
export function rebalanceKeys(count: number): string[] {
  if (count <= 0) return [];
  // k 取使得 36^k >= count+2 的最小整数（多一位防 floor 截断撞同位）
  const k = Math.max(1, Math.ceil(Math.log(count + 2) / Math.log(BASE.length)));
  const total = BASE.length ** k;
  const keys: string[] = [];
  for (let i = 1; i <= count; i++) {
    let n = Math.floor((i * total) / (count + 1));
    let s = '';
    for (let j = 0; j < k; j++) {
      s = BASE[n % BASE.length]! + s;
      n = Math.floor(n / BASE.length);
    }
    keys.push(s);
  }
  return keys;
}
